import { game } from '../main';
import { makeImage } from '../utils/makeImage';
import { createOneLineText, OneLineText } from '../Components/OneLineText';
import { Button } from '../Components/Button';

export interface IButtonLabel {
  group: Phaser.Group;
  button: Button;
  label: Phaser.Text | OneLineText;
}

export interface ISlider {
  getValue(): number;
  changePos(newPos: number): void;
  onSliderMove: Phaser.Signal;
}

export class Popup extends Phaser.Group {
  private gfx: Phaser.Graphics;
  private shadow: Phaser.Image;

  constructor() {
    super(game);

    this.gfx = game.add.graphics();
    this.addChild(this.gfx);
    this.drawWindow(500, 660);
    this.shadow = makeImage(5, 5, 'popup_shadow', 0.5, this);
    this.shadow.scale.set(2);
  }

  public closeWindow() {
    this.destroy();
  }

  public drawWindow(width: number, height: number): void {
    this.gfx.clear();
    this.gfx.beginFill(parseInt(game.config.backgroundColor.slice(1), 16), 1);
    this.gfx.drawRoundedRect(width * -0.5, height * -0.5, width, height, 40);
  }

  public addTitle(text: string): void {
    const header = makeImage(0, -290, 'bg_header', 0.5, this);
    const title = createOneLineText(0, -290, 'font', text, 40, 480, 0.5, 0.5);
    this.addChild(title);
  }

  public addButton(
    x: number,
    y: number,
    sprite: string,
    text: string,
    callback: () => void,
  ) : Button {
    const button = new Button(x, y, sprite, callback);
    button.addTextLabel('font', text, 30);
    this.addChild(button);
    return button;
  }

  public addLabelButton(
    x: number,
    y: number,
    labelText: string,
    labelWidth: number = 400,
    sprite: string = 'checkmark_select',
  ): IButtonLabel {
    const group = game.make.group();
    group.position.setTo(x, y);
    const button = new Button(0, 0, sprite);
    group.add(button);
    const label = createOneLineText(20, 0, 'font', labelText, 20, labelWidth, 0, 0.5);
    group.add(label);
    this.add(group);
    return {
      group,
      label,
      button,
    };
  }

  public addLabel(
    x: number,
    y: number,
    txt: string,
    labelWidth: number = 400,
  ): OneLineText | Phaser.Text {
    const label = createOneLineText(x, y, 'font', txt, 25, labelWidth, 0, 0);
    this.add(label);
    return label;
  }

  public addSlider(
    x: number,
    y: number,
    pos: number,
    range: number,
    pxRange: number,
    sprite: string,
  ): ISlider {
    const line = makeImage(x, y, 'line_color', [0, 0.5]);
    this.addChild(line);
    line.width = pxRange;

    var button = makeImage(x + (pos * pxRange), y, sprite, 0.5);
    button.scale.setTo(0.6);

    const data = {
      picked: false,
      orgX: x,
      range,
      pxRange,
      pos: pos * 100,
      prevPos: pos * 100,
      prevX: 0,
      firstY: 0,
    }

    const onSliderMove = new Phaser.Signal();

    button.inputEnabled = true;
    button.events.onInputDown.add(() => {
      data.picked = true;
      data.prevX = game.input.activePointer.x;
      data.firstY = game.input.activePointer.y;
    });
    button.events.onInputUp.add(() => {
      data.picked = false;
    });

    const getValue = () => Math.floor((data.pos / 100) * data.range);
    
    button.update = function() {
      if (data.picked) {
        this.x -= (data.prevX - game.input.activePointer.x);
        data.prevX = game.input.activePointer.x;
        this.x = Phaser.Math.clamp(this.x, data.orgX, data.orgX + data.pxRange);
        data.pos = ((this.x - data.orgX) / data.pxRange) * 100;

        if (Math.floor(data.prevPos) !== Math.floor(data.pos)) {
          onSliderMove.dispatch(data.pos);
          data.prevPos = data.pos;
        }

        if (Math.abs(game.input.activePointer.y - data.firstY) > this.height * 1.5) {
          data.picked = false;
        }
      }
    };

    const changePos = (newPos) => {
      data.pos = newPos * 100;
      button.x = data.orgX + (data.pxRange * newPos);
    };

    this.addChild(button);

    return {
      getValue,
      changePos,
      onSliderMove,
    };
  }
}